/**
 * Swagger 对比模块
 * 用于比较两个 swagger 文件的差异
 */
import { SwaggerJSON } from '@/define';
import * as Diff from 'diff';

/**
 * API 端点信息
 */
export interface ApiEndpoint {
  path: string; // API 路径
  method: string; // 请求方法
  summary?: string; // 摘要
  description?: string; // 描述
  operation?: any; // 完整的 operation 对象
}

/**
 * 接口修改详情
 */
export interface ModifiedEndpoint {
  endpoint: ApiEndpoint;
  oldOperation: any; // 旧的完整 operation 对象
  newOperation: any; // 新的完整 operation 对象
}

/**
 * Swagger 对比结果
 */
export interface SwaggerComparisonResult {
  added: ApiEndpoint[]; // 新增的接口
  removed: ApiEndpoint[]; // 删除的接口
  modified: ModifiedEndpoint[]; // 修改的接口（包含完整的新旧数据）
}

/**
 * 从 swagger 中提取所有 API 端点
 */
const extractApiEndpoints = (swagger: SwaggerJSON): ApiEndpoint[] => {
  const endpoints: ApiEndpoint[] = [];
  
  if (!swagger.paths) {
    return endpoints;
  }

  Object.keys(swagger.paths).forEach((path) => {
    const pathItem = (swagger.paths as any)[path];
    ['get', 'post', 'put', 'delete', 'patch', 'options', 'head'].forEach((method) => {
      if (pathItem[method]) {
        const operation = pathItem[method] as any;
        endpoints.push({
          path,
          method: method.toUpperCase(),
          summary: operation.summary,
          description: operation.description,
          operation, // 保存完整的 operation 对象用于对比
        });
      }
    });
  });

  return endpoints;
};

/**
 * 生成 API 端点的唯一标识
 */
const getEndpointKey = (endpoint: ApiEndpoint): string => {
  return `${endpoint.method}:${endpoint.path}`;
};

/**
 * 检测两个 operation 对象是否相同
 * 直接对比整个对象的 JSON 序列化结果
 */
const isOperationChanged = (oldOperation: any, newOperation: any): boolean => {
  const oldStr = JSON.stringify(oldOperation, null, 2);
  const newStr = JSON.stringify(newOperation, null, 2);
  return oldStr !== newStr;
};

/**
 * 生成美观的 diff 输出（类似 git diff）
 */
const generatePrettyDiff = (oldOperation: any, newOperation: any): string => {
  const oldStr = JSON.stringify(oldOperation, null, 2);
  const newStr = JSON.stringify(newOperation, null, 2);
  
  const diff = Diff.createPatch('operation', oldStr, newStr, '', '', { context: 3 });
  
  // 将 unified diff 格式转换为 markdown 友好的格式
  const lines = diff.split('\n');
  const diffLines: string[] = [];
  
  // 跳过前4行（文件头信息）
  for (let i = 4; i < lines.length; i++) {
    const line = lines[i];
    if (!line) continue;
    
    if (line.startsWith('+') && !line.startsWith('+++')) {
      // 新增的行，使用绿色
      diffLines.push(`+ ${line.substring(1)}`);
    } else if (line.startsWith('-') && !line.startsWith('---')) {
      // 删除的行，使用红色
      diffLines.push(`- ${line.substring(1)}`);
    } else if (line.startsWith('@@')) {
      // 位置信息
      diffLines.push(`\n${line}\n`);
    } else if (line.startsWith(' ')) {
      // 上下文行
      diffLines.push(`  ${line.substring(1)}`);
    }
  }
  
  return diffLines.join('\n');
};

/**
 * 比较两个 swagger 文件
 */
export const compareSwagger = (oldSwaggerJson: string, newSwaggerJson: string): SwaggerComparisonResult => {
  try {
    const oldSwagger = JSON.parse(oldSwaggerJson) as SwaggerJSON;
    const newSwagger = JSON.parse(newSwaggerJson) as SwaggerJSON;
    
    const oldEndpoints = extractApiEndpoints(oldSwagger);
    const newEndpoints = extractApiEndpoints(newSwagger);
    
    // 创建端点映射
    const oldEndpointMap = new Map<string, ApiEndpoint>();
    oldEndpoints.forEach(ep => oldEndpointMap.set(getEndpointKey(ep), ep));
    
    const newEndpointMap = new Map<string, ApiEndpoint>();
    newEndpoints.forEach(ep => newEndpointMap.set(getEndpointKey(ep), ep));
    
    // 检测新增的接口
    const added: ApiEndpoint[] = [];
    newEndpoints.forEach(ep => {
      if (!oldEndpointMap.has(getEndpointKey(ep))) {
        added.push(ep);
      }
    });
    
    // 检测删除的接口
    const removed: ApiEndpoint[] = [];
    oldEndpoints.forEach(ep => {
      if (!newEndpointMap.has(getEndpointKey(ep))) {
        removed.push(ep);
      }
    });
    
    // 检测修改的接口 - 直接对比整个 operation 对象
    const modified: ModifiedEndpoint[] = [];
    newEndpoints.forEach(newEp => {
      const key = getEndpointKey(newEp);
      const oldEp = oldEndpointMap.get(key);
      if (oldEp) {
        // 直接对比整个 operation 对象
        if (isOperationChanged(oldEp.operation, newEp.operation)) {
          modified.push({
            endpoint: newEp,
            oldOperation: oldEp.operation,
            newOperation: newEp.operation,
          });
        }
      }
    });
    
    return { added, removed, modified };
  } catch (error) {
    throw new Error(`Failed to compare swagger files: ${(error as Error).message}`);
  }
};

/**
 * 生成对比结果的 Markdown 表格
 */
export const generateComparisonMarkdown = (result: SwaggerComparisonResult, includeDetailedDiff = false): string => {
  const sections: string[] = [];
  
  // 新增接口
  if (result.added.length > 0) {
    sections.push('### 新增接口\n');
    sections.push('| 方法 | 路径 | 描述 |');
    sections.push('|------|------|------|');
    result.added.forEach(ep => {
      sections.push(`| ${ep.method} | ${ep.path} | ${ep.description || ep.summary || '-'} |`);
    });
    sections.push('');
  }
  
  // 删除接口
  if (result.removed.length > 0) {
    sections.push('### 删除接口\n');
    sections.push('| 方法 | 路径 | 描述 |');
    sections.push('|------|------|------|');
    result.removed.forEach(ep => {
      sections.push(`| ${ep.method} | ${ep.path} | ${ep.description || ep.summary || '-'} |`);
    });
    sections.push('');
  }
  
  // 修改接口
  if (result.modified.length > 0) {
    sections.push('### 修改接口\n');
    sections.push('| 方法 | 路径 | 描述 |');
    sections.push('|------|------|------|');
    result.modified.forEach(({ endpoint }) => {
      sections.push(`| ${endpoint.method} | ${endpoint.path} | ${endpoint.description || endpoint.summary || '-'} |`);
    });
    sections.push('');
    
    // 如果需要详细 diff，展示每个修改接口的对比
    if (includeDetailedDiff) {
      sections.push('### 修改接口详细变化\n');
      result.modified.forEach(({ endpoint, oldOperation, newOperation }) => {
        sections.push(`#### ${endpoint.method} ${endpoint.path}\n`);
        sections.push('```diff');
        sections.push(generatePrettyDiff(oldOperation, newOperation));
        sections.push('```\n');
      });
    } else {
      sections.push('> **提示**: 以上接口的数据结构发生了变化，请查看详细的 diff 日志');
      sections.push('');
    }
  }
  
  return sections.join('\n');
};

/**
 * 生成包含详细 diff 的日志输出
 */
export const generateDetailedDiffLog = (result: SwaggerComparisonResult): any => {
  const diffLog: any = {
    summary: {
      added: result.added.length,
      removed: result.removed.length,
      modified: result.modified.length,
    },
    details: {
      added: result.added.map(ep => ({
        method: ep.method,
        path: ep.path,
        summary: ep.summary,
        description: ep.description,
      })),
      removed: result.removed.map(ep => ({
        method: ep.method,
        path: ep.path,
        summary: ep.summary,
        description: ep.description,
      })),
      modified: result.modified.map(({ endpoint, oldOperation, newOperation }) => ({
        method: endpoint.method,
        path: endpoint.path,
        summary: endpoint.summary,
        description: endpoint.description,
        diff: {
          old: oldOperation,
          new: newOperation,
        },
      })),
    },
  };
  
  return diffLog;
};

